import { CircleCheck, CircleX, MinusCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatActionType } from './SiteHealthFormat'
import { EYEBROW_CLASS } from './SiteHealthStyles'
import type { RobotsPolicyStatusMetadata } from './SiteHealthStyles.types'
import type { RobotsPolicyMatrixProps } from './siteHealthComponents.types'

function isRobotsPolicyMatrix(
  policy: unknown
): policy is RobotsPolicyMatrixProps['policy'] {
  if (typeof policy !== 'object' || policy === null || !('entries' in policy)) {
    return false
  }

  if (!Array.isArray(policy.entries)) return false

  return policy.entries.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      'userAgent' in entry &&
      typeof entry.userAgent === 'string' &&
      'status' in entry &&
      typeof entry.status === 'string'
  )
}

function statusMetadata(status: string): RobotsPolicyStatusMetadata {
  const normalized = status.toLocaleLowerCase()

  if (normalized.includes('allow')) {
    return {
      icon: CircleCheck,
      className: 'text-emerald-600 dark:text-emerald-400',
      label: 'Allowed',
    }
  }

  if (normalized.includes('block')) {
    return {
      icon: CircleX,
      className: 'text-red-500 dark:text-red-400',
      label: formatActionType(status),
    }
  }

  return {
    icon: MinusCircle,
    className: 'text-muted-foreground',
    label: formatActionType(status),
  }
}

const INNER_CARD_CLASS = 'rounded-xl border border-border bg-background p-4'

export default function RobotsPolicyMatrix({
  policy,
  className,
}: RobotsPolicyMatrixProps) {
  if (!isRobotsPolicyMatrix(policy)) {
    return (
      <section className={cn(INNER_CARD_CLASS, className)}>
        <h3 className="font-semibold">AI crawler robots policy</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Robots policy data is unavailable.
        </p>
      </section>
    )
  }

  const showPurpose = policy.entries.some((entry) => Boolean(entry.purpose))

  return (
    <section className={cn(INNER_CARD_CLASS, className)}>
      <div>
        <h3 className="font-semibold">AI crawler robots policy</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Your robots.txt file says which AI crawlers may access your site.
          Other site controls may still block them.
        </p>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[480px] text-left text-sm">
          <caption className="sr-only">AI crawler robots policy</caption>
          <thead className={EYEBROW_CLASS}>
            <tr>
              <th scope="col" className="border-b border-border px-3 py-2">
                User agent
              </th>
              <th scope="col" className="border-b border-border px-3 py-2">
                Policy
              </th>
              {showPurpose && (
                <th scope="col" className="border-b border-border px-3 py-2">
                  Purpose
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {policy.entries.map((entry) => {
              const metadata = statusMetadata(entry.status)
              const Icon = metadata.icon

              return (
                <tr key={entry.userAgent}>
                  <th scope="row" className="px-3 py-3 font-medium">
                    {entry.userAgent}
                  </th>
                  <td className={`px-3 py-3 ${metadata.className}`}>
                    <span className="inline-flex items-center gap-1.5">
                      <Icon className="size-4" aria-hidden="true" />
                      <span>{metadata.label}</span>
                      {entry.reason && (
                        <span className="text-muted-foreground">
                          · {entry.reason}
                        </span>
                      )}
                    </span>
                  </td>
                  {showPurpose && (
                    <td className="px-3 py-3 text-muted-foreground">
                      {entry.purpose ?? '—'}
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {policy.note && (
        <p className="mt-3 text-xs text-muted-foreground">{policy.note}</p>
      )}
    </section>
  )
}
